"use client";

import type { ClientUserAttribute } from "@rmh/defs";
import { OnboardingStatus } from "@rmh/defs";
import { fetchUserAttributes, fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import {
  useEffect,
  useState,
  useCallback,
  useRef,
  createContext,
  useContext,
} from "react";
import { usePostHog } from "posthog-js/react";
import { identifyPatient } from "@rmh/posthog";
import type { GeneratedQuery } from "@aws-amplify/api-graphql";
import gql from "graphql-tag";
import { fetchGraphQLClient } from "@rmh/data-ops/src/amplify/fetchGraphQLClient";

interface PatientUserAttributesData {
  sub?: string;
  referralId?: string;
  onboardingStatus?: OnboardingStatus;
  firstName?: string;
  nickname?: string;
  lastName?: string;
  email?: string;
  registrationType?: string;
  phoneNumber?: string;
}

interface PatientUserAttributes extends PatientUserAttributesData {
  /**
   * True when these attributes belong to a patient a super user is viewing as,
   * rather than to the signed-in user. Consumers use it to suppress actions
   * that would write on the patient's behalf.
   */
  isImpersonating: boolean;
  /** True while a fetch is in flight (initial load or a refresh). */
  isPending: boolean;
  /** True once attributes have been successfully loaded at least once. */
  isLoaded: boolean;
  /** True when the last load attempt exhausted retries and failed. */
  error: boolean;
  /**
   * Re-read attributes from Cognito. Forces a token refresh by default so
   * attributes written server-side (or by another tab) become visible.
   * Resolves with the freshly loaded data (or null when signed out / failed).
   */
  refresh: (options?: {
    forceRefresh?: boolean;
  }) => Promise<PatientUserAttributesData | null>;
}

// The PostConfirmation trigger provisions the patient asynchronously while the
// client navigates straight into onboarding, so the first read can land before
// custom:OnboardStatus is populated. Retry with backoff to ride out that window.
const MAX_FETCH_ATTEMPTS = 5;
const fetchRetryDelay = (attempt: number) => Math.min(500 * 2 ** attempt, 4000);

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const adminGetUserCognitoAttributesQuery = gql`
  query AdminGetUserCognitoAttributes($sub: String!) {
    adminGetUserCognitoAttributes(sub: $sub) {
      ... on ClientUserAttribute {
        sub
        email
        given_name
        family_name
        referralId
        onboardStatus
      }
    }
  }
`.loc?.source.body as GeneratedQuery<
  { sub: string },
  { adminGetUserCognitoAttributes: ClientUserAttribute }
>;

/**
 * Loads the impersonated patient's Cognito attributes.
 *
 * The resolver behind `adminGetUserCognitoAttributes` only answers Providers
 * and Admins, so the read is authorized server-side — a patient cannot reach
 * this path by setting the impersonation cookie themselves.
 *
 * `phoneNumber` and `registrationType` are deliberately absent: neither is on
 * `ClientUserAttribute`, and both only feed onboarding, which impersonation
 * never enters.
 */
const loadImpersonatedAttributes = async (
  patientId: string
): Promise<PatientUserAttributesData> => {
  const response = await fetchGraphQLClient(
    adminGetUserCognitoAttributesQuery,
    { sub: patientId }
  );
  const patient = response.data?.adminGetUserCognitoAttributes;

  if (!patient?.sub) {
    throw new Error(`No Cognito attributes found for patient ${patientId}`);
  }

  return {
    sub: patient.sub,
    firstName: patient.given_name,
    nickname: patient.given_name,
    lastName: patient.family_name,
    email: patient.email,
    referralId: patient.referralId || undefined,
    onboardingStatus: patient.onboardStatus as OnboardingStatus,
  };
};

const PatientUserAttributesContext = createContext<PatientUserAttributes>({
  sub: undefined,
  referralId: undefined,
  onboardingStatus: undefined,
  firstName: undefined,
  nickname: undefined,
  lastName: undefined,
  email: undefined,
  registrationType: undefined,
  phoneNumber: undefined,
  isImpersonating: false,
  isPending: false,
  isLoaded: false,
  error: false,
  refresh: async () => null,
});

export const PatientUserAttributesProvider = ({
  children,
  impersonatedPatientId,
}: {
  children: React.ReactNode;
  /**
   * When set, the context describes this patient instead of the signed-in
   * user. Resolved server-side from the impersonation cookie, so every
   * `usePatientUserAttributes()` consumer — and therefore every query keyed on
   * `sub` — sees the impersonated patient without knowing about impersonation.
   */
  impersonatedPatientId?: string | null;
}) => {
  const posthog = usePostHog();
  const [data, setData] = useState<PatientUserAttributesData | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPending, setIsPending] = useState(true);
  const [error, setError] = useState(false);

  // Dedupe overlapping loads (initial effect, Hub events, manual refresh): any
  // caller that arrives mid-flight awaits the in-progress promise instead of
  // kicking off a second fetch.
  const loadPromise = useRef<Promise<PatientUserAttributesData | null> | null>(
    null
  );

  const load = useCallback(
    (options?: {
      forceRefresh?: boolean;
    }): Promise<PatientUserAttributesData | null> => {
      if (loadPromise.current) {
        return loadPromise.current;
      }

      const run = async (): Promise<PatientUserAttributesData | null> => {
        setIsPending(true);
        setError(false);

        let lastError: unknown;
        for (let attempt = 0; attempt < MAX_FETCH_ATTEMPTS; attempt++) {
          try {
            const session = await fetchAuthSession({
              forceRefresh: options?.forceRefresh,
            });
            // No tokens means the user is signed out. That is a valid state, not
            // an error, and retrying will not help — clear and stop.
            if (!session.tokens?.accessToken) {
              setData(null);
              setIsLoaded(false);
              return null;
            }

            if (impersonatedPatientId) {
              const impersonated = await loadImpersonatedAttributes(
                impersonatedPatientId
              );
              setData(impersonated);
              setIsLoaded(true);
              // No `identifyPatient` here: the session belongs to the super
              // user, and identifying them as the patient would attribute
              // their clicks to that patient in PostHog.
              return impersonated;
            }

            const userAttributes = await fetchUserAttributes();
            const patientId = userAttributes?.sub;
            const cognitoPatientOnboardingInfo =
              userAttributes?.["custom:OnboardStatus"];
            if (!patientId || !cognitoPatientOnboardingInfo) {
              throw new Error(
                "Patient ID or cognito patient onboarding info not found"
              );
            }

            // Leave registrationType undefined when the attribute is genuinely
            // absent. Defaulting to "self" here would silently mask a missing or
            // not-yet-propagated value and route supported patients incorrectly.
            const registrationType =
              userAttributes?.["custom:RegistrationType"];

            let referralId: string | undefined;
            let onboardingStatus: OnboardingStatus | undefined;
            const onboardingSplit = cognitoPatientOnboardingInfo.split(":");
            if (onboardingSplit.length > 1) {
              referralId = onboardingSplit[0];
              onboardingStatus = onboardingSplit[1] as OnboardingStatus;
            } else {
              onboardingStatus = onboardingSplit[0] as OnboardingStatus;
            }

            const nextData: PatientUserAttributesData = {
              sub: patientId,
              firstName: userAttributes.given_name,
              nickname: userAttributes.given_name,
              lastName: userAttributes.family_name,
              email: userAttributes.email,
              phoneNumber: userAttributes.phone_number,
              referralId,
              onboardingStatus,
              registrationType,
            };

            setData(nextData);
            setIsLoaded(true);

            identifyPatient(posthog, userAttributes.email, {
              name: `${userAttributes.given_name} ${userAttributes.family_name}`,
            });

            return nextData;
          } catch (e) {
            lastError = e;
            if (attempt < MAX_FETCH_ATTEMPTS - 1) {
              await wait(fetchRetryDelay(attempt));
            }
          }
        }

        console.error(lastError);
        setError(true);
        return null;
      };

      const promise = run().finally(() => {
        setIsPending(false);
        loadPromise.current = null;
      });
      loadPromise.current = promise;
      return promise;
    },
    [posthog, impersonatedPatientId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Keep the context in sync with auth lifecycle: a fresh sign-in or token
  // refresh re-reads attributes (force refresh to pull server-side writes), and
  // sign-out clears them so a subsequent user never sees stale data.
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
        case "tokenRefresh":
          load({ forceRefresh: true });
          break;
        case "signedOut":
          setData(null);
          setIsLoaded(false);
          setError(false);
          break;
        default:
          break;
      }
    });
    return unsubscribe;
  }, [load]);

  const refresh = useCallback(
    (options?: { forceRefresh?: boolean }) =>
      load({ forceRefresh: options?.forceRefresh ?? true }),
    [load]
  );

  return (
    <PatientUserAttributesContext.Provider
      value={{
        ...data,
        isImpersonating: Boolean(impersonatedPatientId),
        isPending,
        isLoaded,
        error,
        refresh,
      }}
    >
      {children}
    </PatientUserAttributesContext.Provider>
  );
};

export const usePatientUserAttributes = () =>
  useContext(PatientUserAttributesContext);

export default usePatientUserAttributes;
